import React, { useState, useEffect } from 'react';
import '../styles/Hero.css';
import CartIcon from './CartIcon';
import { useCart, generateKey } from '../context/CartContext';
import { FaTrash } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom'; // For SPA navigation
import { useTranslation } from 'react-i18next';


const images = [
    require('../assets/images/b1.jpg'),
    require('../assets/images/2.jpg'),
    require('../assets/images/1.jpg'),
];


const buttonRoutes = {
    // German
    'RESERVIEREN': '/#ReservationForm',
    'TISCH RESERVIEREN': '/#ReservationForm',
    'ONLINE BESTELLEN': '/#Bestellen',
    'JETZT BESTELLEN': '/#Bestellen',
    'MENÜ ANSEHEN': '/#Bestellen',
    'SPEZIALANGEBOTE': '/#Bestellen',
    // English
    'RESERVE': '/#ReservationForm',
    'BOOK A TABLE': '/#ReservationForm',
    'ORDER ONLINE': '/#Bestellen',
    'ORDER NOW': '/#Bestellen',
    'VIEW MENU': '/#Bestellen',
    'SPECIAL OFFERS': '/#Bestellen',
    // Arabic (example)
    'احجز الآن': '/#ReservationForm',
    'حجز طاولة': '/#ReservationForm',
    'اطلب عبر الإنترنت': '/#Bestellen',
    'اطلب الآن': '/#Bestellen',
    'عرض القائمة': '/#Bestellen',
    'عروض خاصة': '/#Bestellen',
    // Turkish
    'REZERVASYON YAP': '/#ReservationForm',
    'MASA REZERVE ET': '/#ReservationForm',
    'ÇEVRİMİÇİ SİPARİŞ': '/#Bestellen',
    'ŞİMDİ SİPARİŞ VER': '/#Bestellen',
    'MENÜYÜ GÖRÜNTÜLE': '/#Bestellen',
    'ÖZEL TEKLİFLER': '/#Bestellen'
};


const Hero = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showCart, setShowCart] = useState(false);
    const [pulse, setPulse] = useState(false);
    const { cartItems, removeFromCart, increaseQuantity, decreaseQuantity, updateNote } = useCart();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Translation content
    const slideContent = t('hero.slides', { returnObjects: true });

    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentSlide((prev) => (prev + 1) % images.length);
            }, 1500);

            setTimeout(() => {
                setIsTransitioning(false);
            }, 3000);
        }, 7000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (cartItems.length > 0) {
            setPulse(true);
            const timer = setTimeout(() => setPulse(false), 500);
            return () => clearTimeout(timer);
        }
    }, [cartItems]);

    const toggleCart = () => {
        setShowCart((prev) => !prev);
    };

    const handleDelete = (itemId) => {
        removeFromCart(itemId);
    };

    const handleButtonClick = (label) => {
        const route = buttonRoutes[label];
        if (route) {
            const hash = route.split('#')[1];
            if (hash) {
                const el = document.getElementById(hash);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                } else {
                    // fallback: update location hash if element not found
                    window.location.hash = hash;
                }
            }
        } else {
            console.warn(`No route found for button label: "${label}"`);
        }
    };


    const cartTotal = cartItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0
    ).toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR'
    });

    return (
        <div className="hero-container">
            <div
                className={`hero-cart-icon ${pulse ? 'pulse' : ''}`}
                onClick={toggleCart}
                aria-label="Warenkorb anzeigen"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggleCart()}
            >
                <CartIcon itemCount={cartItems.length} />
                <div className={`cart-dropdown ${showCart ? 'show' : ''}`}>
                    <ul>
                        {cartItems.length === 0 ? (
                            <li className="empty-cart">{t('hero.cartEmpty')}</li>
                        ) : (
                            cartItems.map((item) => {
                                const itemKey = generateKey(item);

                                return (
                                    <li key={`cart-item-${itemKey}`}>
                                        <div className="cart-item">
                                            <img src={item.image} alt={item.name} className="cart-item-image" />
                                            <div className="cart-item-details">
                                                <p>{item.name}</p>
                                                <p>{(item.price * item.quantity).toFixed(2)} €</p>

                                                <div className="quantity-controls">
                                                    <button onClick={() => decreaseQuantity(itemKey)} disabled={item.quantity === 1}>-</button>
                                                    <span>{item.quantity}</span>
                                                    <button onClick={() => increaseQuantity(itemKey)}>+</button>
                                                </div>

                                                {/* Textarea for note */}
                                                <textarea
                                                    placeholder={t('hero.notePlaceholder')}
                                                    value={item.note || ''}
                                                    onChange={(e) => updateNote(itemKey, e.target.value)}
                                                    className="cart-item-note"
                                                    rows={2}
                                                />
                                            </div>

                                            <FaTrash onClick={() => handleDelete(itemKey)} className="delete-icon" />
                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>

                    {cartItems.length > 0 && (
                        <>
                            <div className="cart-total">
                                <strong>{t('hero.total')}:</strong>  {cartTotal}
                            </div>
                            <button className="checkout-button" onClick={() => navigate('/checkout')}>
                                {t('hero.checkout')} →
                            </button>
                        </>
                    )}
                </div>
            </div>

            {images.map((img, index) => {
                const isActive = index === currentSlide;
                const isPrevious = index === (currentSlide - 1 + images.length) % images.length;

                return (
                    <div
                        key={`slide-${index}`}
                        className={`hero-slide 
                            ${isActive ? 'active' : ''}
                            ${isActive && isTransitioning ? 'zoom-active' : ''}
                            ${isPrevious && isTransitioning ? 'slide-out' : ''}`}
                        style={{ backgroundImage: `url(${img})` }}
                    />
                );
            })}

            <div className="hero-overlay"></div>

            <div className="hero-content">
                <h1 className={`text-animate ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
                    {slideContent[currentSlide].title}
                </h1>
                <p className={`text-animate ${isTransitioning ? 'fade-out' : 'fade-in'}`} style={{ transitionDelay: '0.3s' }}>
                    {slideContent[currentSlide].subtitle}
                </p>
                <div className={`hero-buttons ${isTransitioning ? 'fade-out' : 'fade-in'}`} style={{ transitionDelay: '0.6s' }}>
                    {slideContent[currentSlide].buttons.map((btn, i) => (
                        <button
                            key={`btn-${i}`}
                            className={i === 0 ? 'primary-button' : 'secondary-button'}
                            onClick={() => handleButtonClick(btn)}
                        >
                            {btn} →
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Hero;
